// The switches on the running robot's board: one button each, and whatever the
// hardware has to warn about.
//
// A tactile switch is momentary, so the button is too: it closes on pointerdown
// and opens again when the pointer is released or leaves it. Nothing is toggled
// and nothing latches -- holding the mouse down is holding the button down, and
// letting go of either lets go of both.
//
// The press goes straight into the controller (`set_switch_at`), which drives
// the pin from outside the chip on its next step. The warning comes back the
// other way and is polled, because it depends on what the sketch has done to
// its pins by now: a firmware that makes a switch pin an output has wired that
// output to a rail, and only the running board knows it.
//
// That warning is the *board's* rather than the switch list's, and it is polled
// here for every input on it: a joystick's wiper on a pin the sketch drives is
// the same mistake, and the controller reports one message for both. So it
// lives in its own box at the foot of the column (`#switch-warning`, outside
// `#switches`), and the poll runs whenever the robot has switches or joysticks
// -- a robot with only joysticks would otherwise never hear about a fault on
// one.

import { AddedRobot, InterfaceHybrid } from "simulator";

// Slower than the serial poll: this is a wiring mistake, not a stream, and it
// only changes when the sketch calls pinMode.
const POLL_MS = 250;

let hybrid: InterfaceHybrid | undefined;
// Which articulated's board is being pressed. As with the editor and the serial
// monitor, that is the most recently added robot.
let robot = 0;
let timer: number | undefined;

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

// Show the switch panel for the robot just added. A robot with no switches
// hides it instead: the panel is a list of buttons and an empty one says
// nothing the drop status has not already said.
export function pressSwitchesOf(scene: InterfaceHybrid, added: AddedRobot) {
  // Let go of anything held on the board being replaced. The press is a state
  // the controller keeps, so a button abandoned mid-press would otherwise stay
  // down for as long as that robot is in the scene.
  releaseAll();

  hybrid = scene;
  robot = added.index;

  const names = added.switchNames();
  const wiring = added.switchWiring();
  // Everything on this board that a pin fault could be about, which is what
  // decides whether the warning is worth polling for -- not just what is
  // listed below.
  const inputs = names.length + added.joystickNames().length;
  const panel = element("switches");
  const list = element("switch-list");
  list.replaceChildren();
  element("switch-warning").hidden = true;

  names.forEach((name, index) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "switch-button";
    const where = document.createElement("span");
    where.textContent = wiring[index] ?? "";
    button.append(name, where);

    const press = (pressed: boolean) => {
      button.classList.toggle("held", pressed);
      setSwitch(index, pressed);
    };
    // Capture the pointer, so a press that wanders off the button still ends on
    // it: without that, releasing outside would leave the switch held down.
    button.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      button.setPointerCapture(event.pointerId);
      press(true);
    });
    button.addEventListener("pointerup", () => press(false));
    button.addEventListener("pointercancel", () => press(false));

    list.append(button);
  });

  panel.hidden = names.length === 0;
  if (inputs === 0) {
    stop();
    return;
  }
  if (timer === undefined) {
    timer = window.setInterval(poll, POLL_MS);
  }
}

// Release every switch on the board being watched, and forget which board that
// was. Called before pointing the panel at another robot.
function releaseAll() {
  const list = document.getElementById("switch-list");
  if (!list || !hybrid) {
    return;
  }
  const buttons = list.querySelectorAll<HTMLButtonElement>(".switch-button");
  buttons.forEach((button, index) => {
    if (button.classList.contains("held")) {
      setSwitch(index, false);
    }
  });
}

function setSwitch(index: number, pressed: boolean) {
  if (!hybrid) {
    return;
  }
  try {
    hybrid.set_switch_at(robot, index, pressed);
  } catch (error) {
    console.error("switches: could not press", error);
  }
}

function poll() {
  if (!hybrid) {
    return;
  }
  const warning = element("switch-warning");
  try {
    const found = hybrid.hardware_warning_at(robot) ?? "";
    if (found !== warning.textContent) {
      warning.textContent = found;
      // Worth the console too, and only as it changes: the panel is 200px wide
      // and the message names a pin and the pinMode to use instead.
      if (found !== "") {
        console.warn(found);
      }
    }
    warning.hidden = found === "";
  } catch (error) {
    // As in the serial monitor: wasm is unusable after a panic, so one crash
    // would otherwise report itself four times a second.
    stop();
    console.error("switches: stopped polling", error);
  }
}

function stop() {
  window.clearInterval(timer);
  timer = undefined;
}
